import axios from 'axios';
import packageJson from '../../package.json';

// Base URL for the external backend
const BASE_URL =
    import.meta.env.VITE_API_ENV === 'local'
        ? 'http://localhost:5054'
        : 'https://api.gallery.barkbyborders.com'

const isClient = typeof window !== 'undefined';

// Utility function to ensure API calls only happen on client side
// This prevents hydration mismatches by returning fallback data during SSR
const clientSideOnly = <T>(fallbackValue: T) => {
    if (!isClient) {
        return Promise.resolve({ data: fallbackValue });
    }
    return null; // Return null to indicate we should proceed with the actual API call
};

// Utility function to reject API calls on server side
const rejectOnServer = (errorMessage: string) => {
    if (!isClient) {
        return Promise.reject(new Error(errorMessage));
    }
    return null; // Return null to indicate we should proceed with the actual API call
};

// Create axios instance with default configuration
const apiClient = axios.create({
    baseURL: BASE_URL,
    timeout: 30000, // 30 seconds timeout
    headers: {
        'Content-Type': 'application/json',
        'x-api-key': import.meta.env.VITE_API_KEY || '',
        'x-app-name': packageJson.name,
        'x-app-version': packageJson.version,
        'Accept': 'application/json',
    },
    withCredentials: true,
});

// Request interceptor to add any common headers or authentication
apiClient.interceptors.request.use(
    (config) => {
        // Only make requests on the client side to avoid SSR issues
        const serverCheck = rejectOnServer('API calls should only be made on the client side');
        if (serverCheck) return serverCheck;

        // Add any authentication tokens or common headers here if needed
        // config.headers.Authorization = `Bearer ${token}`;
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);// Response interceptor to handle common response logic
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // Handle common errors (401, 403, 500, etc.)
        if (isClient) {
            console.error('API Error:', error.response?.data || error.message);
        }
        return Promise.reject(error);
    }
);

// API utility functions
export const api = {
    // Albums endpoints
    albums: {
        // GET /albums - Fetch albums with optional filtering and pagination
        getAll: (params?: {
            isFeatured?: boolean;
            page?: number;
            limit?: number;
        }) => {
            const fallback = clientSideOnly({ albums: [], totalAlbums: 0, totalPages: 0, currentPage: 1 });
            if (fallback) return fallback;

            const searchParams = new URLSearchParams();
            if (params?.isFeatured !== undefined) {
                searchParams.append('isFeatured', params.isFeatured.toString());
            }
            if (params?.page) {
                searchParams.append('page', params.page.toString());
            }
            if (params?.limit) {
                searchParams.append('limit', params.limit.toString());
            }

            const url = `/albums${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
            return apiClient.get(url);
        },

        // GET /albums/:id - Fetch a specific album with photos
        getById: (id: number, params?: {
            page?: number;
            limit?: number;
        }) => {
            const fallback = clientSideOnly({ album: null, totalPhotos: 0, totalPages: 0, currentPage: 1 });
            if (fallback) return fallback;

            const searchParams = new URLSearchParams();
            if (params?.page) {
                searchParams.append('page', params.page.toString());
            }
            if (params?.limit) {
                searchParams.append('limit', params.limit.toString());
            }

            const url = `/albums/${id}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
            return apiClient.get(url);
        },

        // POST /albums - Create a new album
        create: (formData: FormData) => {
            const serverCheck = rejectOnServer('Album creation only available on client side');
            if (serverCheck) return serverCheck;

            return apiClient.post('/albums', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 300000, // 5 minutes for file uploads
            });
        },
    },

    // Files endpoints
    files: {
        // GET /files - List all files
        getAll: () => {
            const fallback = clientSideOnly([]);
            if (fallback) return fallback;

            return apiClient.get('/files');
        },

        // GET /files/:key - Download a specific file
        getByKey: (key: string) => {
            const serverCheck = rejectOnServer('File download only available on client side');
            if (serverCheck) return serverCheck;

            return apiClient.get(`/files/${encodeURIComponent(key)}`, {
                responseType: 'blob', // For file downloads
            });
        },
    },

    // Status endpoint
    status: {
        // GET /status - Health check
        check: () => {
            const fallback = clientSideOnly({ status: 'ok' });
            if (fallback) return fallback;

            return apiClient.get('/status');
        },
    },
};

export default apiClient;
